import classnames from 'classnames/bind';
import { Menu, MenuDropdownProps, Modal } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';

import Pipeline from 'types/pipeline';

import { downloadFile } from 'services/download_file';

import { useModal } from 'hooks/useModal';
import { useAppRedirect } from 'hooks/useAppRedirect';
import { useCreatePipeline } from 'pipelines/hooks/useCreatePipeline';
import { useDeletePipelines } from 'pipelines/hooks/useDeletePipelines';

import { Icon } from 'components/Icon/IconDeprecated';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';
import { Text } from 'components/Text/Text';
import { Tooltip } from 'components/Tooltip/Tooltip';

import { usePipelineDetailStateOptional } from 'pipelines/context/pipeline_detail_view';
import styles from './Actions.module.scss';

const c = classnames.bind(styles);

export type PipelineActionsProps = Pick<MenuDropdownProps, 'children'> & {
  pipeline?: Pipeline;
};

export function PipelineActions({ pipeline, children }: PipelineActionsProps) {
  const redirect = useAppRedirect();
  const { copied: isCopied, copy } = useClipboard();

  const pipelineState = usePipelineDetailStateOptional();

  const { open } = useModal();

  const { mutate: createPipeline, isLoading } = useCreatePipeline({
    onSuccess: (pipeline) => {
      redirect(`/design/pipelines/${pipeline.id}`);
    },
  });

  const { mutateAsync: deletePipelines } = useDeletePipelines({
    onMutate: () => redirect('/design/pipelines'),
  });

  if (!pipeline || !pipeline.hasId()) {
    return null;
  }

  function downloadPipeline() {
    if (!pipeline) {
      return;
    }

    downloadFile(
      `lumeo-pipeline_${pipeline.name.replaceAll(' ', '_')}.json`,
      new Blob([JSON.stringify(pipeline.definition)], {
        type: 'application/json',
      })
    );
  }

  function duplicatePipeline() {
    if (!pipeline) {
      return null;
    }

    createPipeline({
      name: `${pipeline.name} copy`,
      definition: pipeline.definition,
    });
  }

  function handleDeletePipeline() {
    if (!pipeline) {
      return;
    }

    open('confirm-delete', {
      title: `Are you sure you want to delete ${pipeline.name}?`,
      onConfirm: () => deletePipelines({ pipeline_ids: [pipeline.id] }),
    });
  }

  function copyID() {
    if (!pipeline?.id) {
      return;
    }

    copy(pipeline.id);
  }

  return (
    <div className={c('more')}>
      <Tooltip content="Copied ID to clipboard!" open={isCopied}>
        <div>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <IconButton
                icon="more-vertical"
                label="Reveal more actions"
                size="small"
                variant="tertiary"
              />
            </Menu.Target>

            <Menu.Dropdown>
              {children}
              <Menu.Item
                onClick={() => downloadPipeline()}
                leftSection={<Icon name="export" size="small" unstyled />}
              >
                Export as JSON
              </Menu.Item>
              <Menu.Item
                onClick={() => duplicatePipeline()}
                leftSection={<Icon name="copy" size="small" unstyled />}
              >
                Duplicate
              </Menu.Item>
              <Menu.Item
                onClick={copyID}
                leftSection={<Icon name="info" size="small" unstyled />}
              >
                Copy ID
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                color="red"
                onClick={handleDeletePipeline}
                disabled={pipelineState?.isLocked}
                leftSection={<Icon name="delete" size="small" unstyled />}
              >
                Delete pipeline
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </Tooltip>

      <Modal opened={isLoading} onClose={() => {}} withCloseButton={false}>
        <div className="loading-spinner is-small"></div>
        <Text className="loading-spinner-message">Duplicating pipeline...</Text>
      </Modal>
    </div>
  );
}
